<template>
  <div class="vmware-page">
    <!-- Header -->
    <el-header class="header">
      <div class="header-content">
        <div class="logo" @click="goHome">
          <span class="logo-text">51软件下载</span>
        </div>
        <el-menu 
          mode="horizontal" 
          class="nav-menu" 
          background-color="transparent" 
          text-color="#1890ff"
          active-text-color="#1890ff"
          :default-active="activeMenu"
          @select="handleMenuSelect"
          :ellipsis="false"
        >
          <el-menu-item index="/vmware" :style="{ color: '#666' }">首页</el-menu-item>
          <el-menu-item index="/order-query" :style="{ color: '#666' }">订单查询</el-menu-item>
          <el-menu-item index="/feedback" :style="{ color: '#666' }">意见反馈</el-menu-item>
        </el-menu>
      </div>
    </el-header>

    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-content">
        <div class="hero-top">
          <div class="logo-container">
            <img :src="vmwareLogo" alt="VMware Logo" class="vmware-logo">
          </div>
          <h1 class="hero-title">VMware虚拟机</h1>
          <p class="hero-subtitle">🖥️ 专业虚拟化解决方案，高效稳定，功能强大</p>
          <p class="hero-description">多系统并行运行 专业虚拟化技术 - 助力开发测试，提升工作效率</p>

          <div class="hero-actions">
            <el-button type="primary" size="large" class="download-btn" @click="downloadVMware">
              <el-icon><Download /></el-icon>
              立即下载
            </el-button>
            <p class="system-info">高速下载通道，感受更快捷安全的下载体验</p>
          </div>
        </div>
        
        <div class="hero-bottom">
          <div class="software-screenshot">
            <div class="carousel-container" @mouseenter="stopCarousel" @mouseleave="startCarousel">
              <div class="carousel-wrapper">
                <div class="carousel-slide" :class="{ active: currentSlide === 0 }">
                  <img src="../assets/vmware-preview1.jpg" 
                       alt="VMware界面展示1" class="vmware-interface"
                       @error="handleImageError"
                       @load="checkImageLoaded">
                </div>
                <div class="carousel-slide" :class="{ active: currentSlide === 1 }">
                  <img src="../assets/vmware-preview2.png" 
                       alt="VMware界面展示2" class="vmware-interface"
                       @error="handleImageError"
                       @load="checkImageLoaded">
                </div>
              </div>
              <div class="carousel-indicators">
                <div 
                  v-for="(slide, index) in 2" 
                  :key="index"
                  class="indicator"
                  :class="{ active: currentSlide === index }"
                  @click="currentSlide = index"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 四大特性展示 -->
    <div class="features-showcase">
      <div class="features-showcase-grid">
        <div class="showcase-item">
          <span class="showcase-icon">🖥️</span>
          <h3 class="showcase-title">虚拟化</h3>
          <p class="showcase-desc">多系统同时运行<br>高效虚拟化技术</p>
        </div>
        <div class="showcase-item">
          <span class="showcase-icon">⚡</span>
          <h3 class="showcase-title">高性能</h3>
          <p class="showcase-desc">优化资源分配<br>流畅运行体验</p>
        </div>
        <div class="showcase-item">
          <span class="showcase-icon">🛡️</span>
          <h3 class="showcase-title">安全隔离</h3>
          <p class="showcase-desc">系统完全隔离<br>安全可靠保障</p>
        </div>
        <div class="showcase-item">
          <span class="showcase-icon">🔧</span>
          <h3 class="showcase-title">专业工具</h3>
          <p class="showcase-desc">完整功能集合<br>满足专业需求</p>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="section features-section">
      <div class="section-header">
        <h2 class="section-title">软件特性</h2>
        <p class="section-description">VMware提供了一系列高级功能，使您的虚拟化体验更加高效便捷</p>
      </div>
      
      <div class="features-grid">
        <div class="feature-card" v-for="(feature, index) in features" :key="index">
          <div class="feature-icon">
            <el-icon :size="40"><component :is="feature.icon" /></el-icon>
          </div>
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-description">{{ feature.description }}</p>
        </div>
      </div>
      
      <div class="download-highlight">
        <h3>💡 体验更多强大功能</h3>
        <div class="features-download">
          <el-button type="primary" size="large" class="download-btn" @click="downloadVMware">
            <el-icon><Download /></el-icon>
            立即下载体验
          </el-button>
          <p class="features-download-tip">高速下载通道，感受更快捷安全的下载体验</p>
        </div>
      </div>
    </div>

    <!-- Advantages Section -->
    <div class="section advantages-section">
      <div class="section-header">
        <h2 class="section-title">为什么选择VMware</h2>
        <p class="section-description">全球数百万用户的共同选择</p>
      </div>
      
      <div class="advantages-grid">
        <div class="advantage-card" v-for="(advantage, index) in advantages" :key="index">
          <div class="advantage-icon">
            <el-icon :size="32"><component :is="advantage.icon" /></el-icon>
          </div>
          <h3 class="advantage-title">{{ advantage.title }}</h3>
          <p class="advantage-description">{{ advantage.description }}</p>
        </div>
      </div>
    </div>

    <!-- Testimonials Section -->
    <div class="section testimonials-section">
      <div class="section-header">
        <h2 class="section-title">用户真实评价</h2>
        <p class="section-description">听听全球用户对VMware的评价</p>
      </div>
      
      <div class="testimonials-grid">
        <div class="testimonial-card" v-for="(testimonial, index) in testimonials" :key="index">
          <div class="testimonial-header">
            <div class="testimonial-avatar">{{ testimonial.avatar }}</div>
            <div class="testimonial-info">
              <h4>{{ testimonial.name }}</h4>
              <div class="testimonial-rating">★★★★★</div>
            </div>
          </div>
          <div class="testimonial-content">
            <p>{{ testimonial.content }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="section faq-section">
      <div class="section-header">
        <h2 class="section-title">常见问题</h2>
        <p class="section-description">您可能关心的问题</p>
      </div>
      
      <div class="faq-container">
        <div 
          class="faq-item" 
          :class="{ active: activeFaq === index }" 
          v-for="(faq, index) in faqs" 
          :key="index"
          @click="activeFaq = activeFaq === index ? -1 : index"
        >
          <div class="faq-question">{{ faq.question }}</div>
          <div class="faq-answer">{{ faq.answer }}</div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="cta-section">
      <h2>立即体验专业虚拟化</h2>
      <p>提升您的工作效率，享受更强大的虚拟化技术和更稳定的服务</p>
      <el-button type="primary" size="large" class="cta-button" @click="downloadVMware">
        <el-icon><Download /></el-icon>
        立即下载 VMware
      </el-button>
    </div>

    <!-- Footer -->
    <el-footer class="footer">
      <div class="footer-content">
        <p class="copyright">Copyright©2025 51softdown.cn All rights reserved.</p>
        <div class="footer-links">
          <span>奇安科技有限公司</span>
          <span>豫ICP备2025146548号</span>
        </div>
      </div>
    </el-footer>
    
    <!-- 客服联系方式 -->
    <div class="customer-service">
      <div class="service-toggle" @click="toggleServicePanel">
        <el-icon><Service /></el-icon>
        <span>{{ isServicePanelOpen ? '收起客服' : '联系客服' }}</span>
      </div>
      <div class="service-content" :class="{ 'expanded': isServicePanelOpen }">
        <div class="service-header">
          <span>客服咨询</span>
        </div>
        <div class="service-body">
          <div class="qrcode-container">
            <img src="../assets/wechat.png" alt="企业微信二维码" class="qrcode-img">
          </div>
          <div class="service-info">
            <p>企业微信</p>
            <a href="https://work.weixin.qq.com/kfid/kfc8d0f9bc2d1044192" target="_blank" class="service-link">
              点击添加客服
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
/* eslint-disable no-unused-vars */
import { 
  Download,
  Service,
  Monitor,
  Shield,
  Cpu,
  Tools,
  Computer,
  Bolt,
  Camera,
  Network,
  Copy,
  Lock,
  Connection,
  CopyDocument
} from '@element-plus/icons-vue'
/* eslint-enable no-unused-vars */

// 引入VMware Logo
import vmwareLogo from '../assets/vmware.png'

// 路由相关
const route = useRoute()
const router = useRouter()

// 计算当前激活的菜单项
const activeMenu = computed(() => {
  if (route.path === '/order-query') {
    return '/order-query'
  } else if (route.path === '/about') {
    return '/about'
  } else if (route.path === '/idm') {
    return '/idm'
  } else if (route.path === '/vmware') {
    return '/vmware'
  } else if (route.path === '/feedback') {
    return '/feedback'
  }
  return '/'
})

// 菜单选择处理
const handleMenuSelect = (key) => {
  if (key === '/') {
    router.push('/')
  } else if (key === '/idm') {
    router.push('/idm')
  } else if (key === '/vmware') {
    router.push('/vmware')
  } else if (key === '/order-query') {
    router.push('/order-query')
  } else if (key === '/about') {
    router.push('/about')
  } else if (key === '/feedback') {
    router.push('/feedback')
  }
}

// 返回首页
const goHome = () => {
  router.push('/')
}

// 下载VMware
const downloadVMware = () => {
  // 发送Umami自定义事件 - VMware下载
  if (window.umami) {
    window.umami.track('download-vmware', {
      type: 'software',
      name: 'VMware',
      version: 'latest',
      url: 'https://file.51softdown.cn/download/vmware/VMware17.exe'
    });
  }
  
  // 实际的下载逻辑
  window.open('https://file.51softdown.cn/download/vmware/VMware17.exe', '_blank');
}

// 功能特性数据
const features = ref([
  {
    icon: 'Monitor',  // 多系统支持使用显示器图标
    title: '多系统支持',
    description: '在同一台PC上同时运行Windows、Linux、macOS等多种操作系统'
  },
  {
    icon: 'Cpu',  // 将Bolt改为Cpu，表示高性能计算
    title: '高性能',
    description: '优化的虚拟化引擎，提供接近原生的运行性能'
  },
  {
    icon: 'Lock',  // 将Shield改为Lock，表示安全锁定
    title: '安全隔离',
    description: '完全隔离的虚拟环境，保护主机系统安全'
  },
  {
    icon: 'Camera',  // 快照管理使用相机图标
    title: '快照管理',
    description: '强大的快照功能，随时保存和恢复虚拟机状态'
  },
  {
    icon: 'Connection',  // 将Network改为Connection，表示网络连接
    title: '网络配置',
    description: '灵活的网络配置选项，支持多种网络模式'
  },
  {
    icon: 'CopyDocument',  // 将Copy改为CopyDocument，表示文档复制
    title: '克隆复制',
    description: '快速克隆虚拟机，节省部署时间，提高工作效率'
  }
])

// 优势数据
const advantages = ref([
  {
    icon: 'Monitor',
    title: '专业虚拟化',
    description: '业界领先的虚拟化技术，提供企业级虚拟化体验'
  },
  {
    icon: 'Lock',  // 将Shield改为Lock，因为Shield可能不存在
    title: '安全可靠',
    description: '完全隔离的虚拟环境，确保主机系统安全'
  },
  {
    icon: 'Cpu',
    title: '高性能',
    description: '优化资源分配，提供流畅的运行体验'
  },
  {
    icon: 'Tools',
    title: '功能丰富',
    description: '完整的功能集合，满足专业用户需求'
  }
])

// 用户评价数据
const testimonials = ref([
  {
    content: "作为开发人员，VMware让我能够在同一台机器上测试多个操作系统，大大提高了工作效率。快照功能特别实用，可以随时回滚到之前的状态。",
    avatar: "张工",
    name: "张工程师",
    role: "软件开发"
  },
  {
    content: "VMware的网络配置功能非常强大，可以模拟复杂的网络环境，对我们的架构设计和测试工作帮助很大。企业部署选择VMware是明智的。",
    avatar: "李经理",
    name: "李系统管理员",
    role: "IT经理"
  },
  {
    content: "学习不同操作系统的好工具，可以在Windows上运行Linux进行学习和实验，非常方便。虚拟化技术很成熟，性能表现优秀。",
    avatar: "王学",
    name: "王学生",
    role: "计算机专业"
  },
  {
    content: "作为测试人员，VMware是我工作中不可缺少的工具。快照功能太实用了，测试软件时可以随时回滚，不用担心系统被搞坏。",
    avatar: "赵测",
    name: "赵测试员",
    role: "软件测试"
  },
  {
    content: "企业部署选择VMware是明智的，稳定性和兼容性都很出色。克隆功能很实用，可以快速部署相同配置的虚拟机，节省大量时间。",
    avatar: "刘管",
    name: "刘运维工程师",
    role: "系统管理"
  },
  {
    content: "VMware是虚拟化领域的标杆产品，技术成熟，支持完善，值得信赖。隔离性很好，在虚拟机中进行安全测试不会影响主机系统，很安全。",
    avatar: "陈安",
    name: "陈安全专家",
    role: "信息安全"
  }
])

// FAQ数据
const faqs = ref([
  {
    question: "VMware支持哪些操作系统？",
    answer: "VMware支持Windows、Linux、macOS等多种主流操作系统，涵盖几乎所有常用的操作系统版本。"
  },
  {
    question: "虚拟机运行会影响主机性能吗？",
    answer: "VMware采用优化的虚拟化技术，合理分配资源，在保证虚拟机性能的同时，将对主机的影响降到最低。建议为虚拟机分配适当的内存和CPU资源。"
  },
  {
    question: "如何在虚拟机和主机之间传输文件？",
    answer: "可以通过共享文件夹、拖拽功能、网络传输等多种方式在虚拟机和主机之间方便地传输文件。"
  },
  {
    question: "VMware是否安全？",
    answer: "是的，VMware提供完全隔离的虚拟环境，虚拟机中的操作不会影响主机系统。即使虚拟机中感染病毒，也不会传播到主机。"
  },
  {
    question: "快照功能如何使用？",
    answer: "快照功能可以保存虚拟机当前状态，随时可以恢复到快照时的状态，非常适合测试和开发场景。在进行重要操作前创建快照，可以确保能够回滚到安全状态。"
  }
])

// 激活的FAQ项
const activeFaq = ref(0)

// 轮播图相关
const currentSlide = ref(0)

// 自动轮播
let carouselInterval = null

// 开始轮播
const startCarousel = () => {
  carouselInterval = setInterval(() => {
    currentSlide.value = (currentSlide.value + 1) % 3; // 循环切换0、1和2
  }, 3000)
}

// 停止轮播
const stopCarousel = () => {
  if (carouselInterval) {
    clearInterval(carouselInterval)
    carouselInterval = null
  }
}

// 图片加载错误处理
const handleImageError = (event) => {
  console.error('图片加载失败:', event.target.src)
  // 设置默认背景色或占位符
  event.target.style.backgroundColor = '#f8f9fa'
  event.target.alt = '图片加载失败'
}

// 添加一个方法来检查图片是否正确加载
const checkImageLoaded = (event) => {
  console.log('图片加载成功:', event.target.src)
}

// 组件挂载时开始轮播和设置页面标题
onMounted(() => {
  startCarousel()
  document.title = 'VMware虚拟机-专业虚拟化解决方案，高效稳定，功能强大'
  let favicon = document.querySelector('link[rel="icon"]')
  if (favicon) {
    favicon.href = '/vmware.ico'
  } else {
    // 如果页面中没有 favicon link 标签，则创建一个
    favicon = document.createElement('link')
    favicon.rel = 'icon'
    favicon.href = '/vmware.ico'
    document.head.appendChild(favicon)
  }
})

onUnmounted(() => {
  stopCarousel()
})

// 客服面板相关
const isServicePanelOpen = ref(false)

const toggleServicePanel = () => {
  isServicePanelOpen.value = !isServicePanelOpen.value
}
</script>

<style scoped>
.vmware-page {
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto; /* 确保可以滚动查看所有内容 */
}

/* Header Styles */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  padding: 0;
  height: 80px !important;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}

.logo {
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logo:hover {
  color: #1890ff;
}

.logo-text {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  background: linear-gradient(135deg, #1890ff, #40a9ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-menu {
  border: none;
  background: transparent !important;
}

.nav-menu .el-menu-item {
  font-weight: 500;
  padding: 0 20px;
  font-size: 16px;
  color: #666 !important;
}

.nav-menu .el-menu-item:hover {
  color: #1890ff !important;
  background-color: rgba(24, 144, 255, 0.05) !important;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  color: #333;
  padding: 100px 0;
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 50%, rgba(24, 144, 255, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 70% 50%, rgba(64, 169, 255, 0.05) 0%, transparent 50%);
  opacity: 0.8;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
}

.hero-top {
  max-width: 900px;
  margin-bottom: 20px;
}

/* Logo and Title Container */
.logo-container {
  flex-shrink: 0;
}

.vmware-logo-container {
  flex-shrink: 0;
}

.vmware-logo {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  animation: pulse 2s infinite;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.vmware-logo:hover {
  transform: scale(1.1);
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 8px 30px rgba(24, 144, 255, 0.3);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
}

/* Hero Title Styles */
.hero-title {
  font-size: 72px;
  margin-bottom: 40px;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  line-height: 1.2;
}

.hero-title .highlight {
  color: #1890ff;
}

.hero-subtitle {
  font-size: 32px;
  margin-bottom: 25px;
  color: #1890ff;
  font-weight: 600;
  line-height: 1.3;
}

.hero-description {
  font-size: 22px;
  margin-bottom: 50px;
  color: #666;
  line-height: 1.6;
  font-weight: 400;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.hero-features {
  margin-bottom: 40px;
}

.hero-features ul {
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.hero-features li {
  background: rgba(255, 255, 255, 0.8);
  padding: 20px;
  border-radius: 12px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(24, 144, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.hero-features li::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.hero-features li:hover::before {
  transform: scaleX(1);
}

.hero-features li:hover {
  transform: translateY(-5px);
  background: rgba(24, 144, 255, 0.05);
  border-color: rgba(24, 144, 255, 0.3);
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.15);
}

.feature-icon {
  font-size: 24px;
  margin-right: 10px;
  display: inline-block;
  animation: float 3s ease-in-out infinite;
}

.hero-features li:nth-child(1) .feature-icon {
  animation-delay: 0s;
}

.hero-features li:nth-child(2) .feature-icon {
  animation-delay: 0.5s;
}

.hero-features li:nth-child(3) .feature-icon {
  animation-delay: 1s;
}

.hero-features li:nth-child(4) .feature-icon {
  animation-delay: 1.5s;
}

.hero-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-bottom: 0;
}

.download-btn {
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  color: white;
  border: none;
  padding: 30px 100px;
  border-radius: 8px;
  font-size: 26px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.4);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-width: 400px;
  animation: pulse-glow 2.5s infinite;
  letter-spacing: 1px;
  text-align: center;
}

@keyframes pulse-glow {
  0% {
    box-shadow: 0 8px 24px rgba(24, 144, 255, 0.4);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 12px 36px rgba(24, 144, 255, 0.6);
    transform: scale(1.03);
  }
  100% {
    box-shadow: 0 8px 24px rgba(24, 144, 255, 0.4);
    transform: scale(1);
  }
}

.download-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.download-btn:hover::before {
  left: 100%;
}

.download-btn:hover {
  background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
  transform: translateY(-5px) scale(1.08);
  box-shadow: 0 12px 40px rgba(24, 144, 255, 0.6);
  animation: none;
}

.system-info {
  font-size: 18px;
  color: #1890ff;
  text-align: center;
  line-height: 1.6;
  max-width: 500px;
  font-weight: 500;
  opacity: 0.9;
  margin-top: 20px;
}

.hero-top {
  max-width: 900px;
  margin-bottom: 20px;
}

.hero-bottom {
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  padding: 0;
  backdrop-filter: none;
  max-width: 821px;
  margin: 80px auto 0;
  height: 500px;
  width: 100%; /* 确保容器有明确宽度 */
}

.software-screenshot {
  width: 100%;
  max-width: 821px;
  height: 500px;
  overflow: hidden;
  animation: float 6s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  min-width: 100%; /* 确保容器有最小宽度 */
}

/* 轮播图样式 */
.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 100%; /* 确保容器有最小宽度 */
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  min-width: 100%;
  opacity: 0; /* 默认隐藏 */
  transition: opacity 0.3s ease; /* 添加淡入淡出效果 */
}

.carousel-slide.active {
  z-index: 2;
  opacity: 1; /* 激活时显示 */
}

.carousel-slide img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  display: block;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background: #1890ff;
  transform: scale(1.2);
}

.indicator:hover {
  background: rgba(24, 144, 255, 0.8);
}

.vmware-interface {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  display: block;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

/* 四大特性展示 */
.features-showcase {
  padding: 80px 0;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  margin-top: 60px;
}

.features-showcase::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 50%, rgba(24, 144, 255, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 50%, rgba(64, 169, 255, 0.05) 0%, transparent 50%);
  opacity: 0.8;
}

.features-showcase-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
}

.showcase-item {
  text-align: center;
  padding: 50px 30px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(24, 144, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.showcase-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.showcase-item:hover::before {
  transform: scaleX(1);
}

.showcase-item:hover {
  transform: translateY(-10px);
  background: rgba(24, 144, 255, 0.05);
  border-color: rgba(24, 144, 255, 0.3);
  box-shadow: 0 8px 32px rgba(24, 144, 255, 0.2);
}

.showcase-icon {
  font-size: 48px;
  margin-bottom: 20px;
  display: block;
  color: #1890ff;
}

.showcase-title {
  font-size: 24px;
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 15px;
}

.showcase-desc {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
}

/* Section Styles */
.section {
  padding: 80px 0;
  position: relative;
}

.section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 20%, rgba(24, 144, 255, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(64, 169, 255, 0.03) 0%, transparent 50%);
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
}

.section-title {
  text-align: center;
  font-size: 42px;
  margin-bottom: 60px;
  color: #333;
  font-weight: 700;
  position: relative;
  z-index: 1;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
}

.section-description {
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto;
  opacity: 0.8;
  color: #666;
}

/* Features Section */
.features-section {
  background: #f8f9fa;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.feature-card {
  background: rgba(255, 255, 255, 0.9);
  padding: 40px 30px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(24, 144, 255, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 32px rgba(24, 144, 255, 0.2);
  border-color: rgba(24, 144, 255, 0.3);
  background: rgba(24, 144, 255, 0.05);
}

.feature-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  box-shadow: 0 4px 16px rgba(24, 144, 255, 0.3);
  transition: all 0.3s ease;
  color: white;
  position: relative;
  z-index: 2;
}

.feature-card:hover .feature-icon {
  transform: scale(1.1) rotateY(360deg);
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.4);
}

.feature-title {
  font-size: 24px;
  margin-bottom: 15px;
  color: #333;
  font-weight: 600;
}

.feature-description {
  color: #666;
  line-height: 1.8;
  font-size: 16px;
}

/* 下载突出区域 */
.download-highlight {
  padding: 50px 40px;
  margin: 0px auto;
  max-width: 600px;
  text-align: center;
  position: relative;
}

.download-highlight h3 {
  color: #1890ff;
  font-size: 24px;
  margin-bottom: 20px;
  font-weight: 700;
}

.features-download {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.features-download .download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.features-download-tip {
  font-size: 18px;
  color: #1890ff;
  text-align: center;
  line-height: 1.6;
  max-width: 500px;
  font-weight: 500;
  opacity: 0.9;
  margin: 0;
}

/* Advantages Section */
.advantages-section {
  background: #ffffff;
}

.advantages-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  padding: 0 20px;
  position: relative;
  z-index: 1;
}

.advantage-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(24, 144, 255, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
}

.advantage-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.advantage-card:hover::before {
  transform: scaleX(1);
}

.advantage-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 32px rgba(24, 144, 255, 0.2);
  background: rgba(24, 144, 255, 0.05);
  border-color: rgba(24, 144, 255, 0.3);
}

.advantage-icon {
  width: 70px;
  height: 70px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1890ff, #40a9ff);
  border-radius: 50%;
  color: white;
  font-size: 32px;
}

.advantage-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

.advantage-description {
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
}

/* Testimonials Section */
.testimonials-section {
  background: #f8f9fa;
}

.testimonials-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  padding: 0 20px;
  position: relative;
  z-index: 1;
  margin-bottom: 60px;
}

.testimonial-card {
  background: rgba(255, 255, 255, 0.9);
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(24, 144, 255, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
}

.testimonial-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.testimonial-card:hover::before {
  transform: scaleX(1);
}

.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 32px rgba(24, 144, 255, 0.2);
  background: rgba(24, 144, 255, 0.05);
  border-color: rgba(24, 144, 255, 0.3);
}

.testimonial-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.testimonial-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 15px;
  background: linear-gradient(135deg, #1890ff, #40a9ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 18px;
}

.testimonial-info h4 {
  font-size: 16px;
  color: #333;
  margin-bottom: 5px;
}

.testimonial-rating {
  color: #ffa940;
  font-size: 14px;
}

.testimonial-content {
  color: #666;
  line-height: 1.6;
  font-size: 14px;
}

.testimonial-content p {
  margin: 0;
}

/* FAQ Section */
.faq-section {
  background: #ffffff;
  padding: 80px 0;
  position: relative;
}

.faq-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
}

.faq-item {
  background: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(24, 144, 255, 0.1);
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.faq-question {
  padding: 25px 30px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
}

.faq-question:hover {
  background: rgba(24, 144, 255, 0.05);
  color: #1890ff;
}

.faq-question::after {
  content: '+';
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 24px;
  font-weight: bold;
  color: #1890ff;
  transition: transform 0.3s ease;
}

.faq-item.active .faq-question::after {
  transform: translateY(-50%) rotate(45deg);
}

.faq-answer {
  padding: 0 30px 25px;
  color: #666;
  line-height: 1.8;
  display: none;
}

.faq-item.active .faq-answer {
  display: block;
}

/* CTA Section */
.cta-section {
  text-align: center;
  padding: 100px 20px;
  background: linear-gradient(135deg, #1890ff, #00cc99);
  color: white;
  position: relative;
  overflow: hidden;
}

.cta-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

.cta-section h2 {
  font-size: 2.5rem;
  margin-bottom: 20px;
  font-weight: 700;
  position: relative;
  z-index: 1;
}

.cta-section p {
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 40px;
  opacity: 0.9;
  position: relative;
  z-index: 1;
}

.cta-button {
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  color: white;
  border: none;
  padding: 30px 100px;
  border-radius: 8px;
  font-size: 26px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.4);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-width: 400px;
  animation: pulse-glow 2.5s infinite;
  letter-spacing: 1px;
  z-index: 1;
  text-align: center;
}

.cta-button:hover {
  background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
  transform: translateY(-5px) scale(1.08);
  box-shadow: 0 12px 40px rgba(24, 144, 255, 0.6);
  animation: none;
}

/* Footer */
.footer {
  background: #f8f9fa;
  color: #333;
  padding: 40px 0 20px;
  text-align: center;
  border-top: 1px solid rgba(24, 144, 255, 0.1);
  height: auto !important;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.copyright {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #333;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 30px;
  font-size: 12px;
  color: #999;
  flex-wrap: wrap;
}

/* 客服联系方式 */
.customer-service {
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
}

.service-toggle {
  background: linear-gradient(135deg, #1890ff, #40a9ff);
  color: white;
  text-align: center;
  padding: 12px 0;
  font-weight: bold;
  border-radius: 10px 10px 0 0;
  cursor: pointer;
  width: 180px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  font-size: 14px;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.service-toggle:hover {
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  box-shadow: 0 6px 24px rgba(24, 144, 255, 0.3);
  transform: translateY(-2px);
}

.service-content {
  background: white;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 180px;
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;
}

.service-content.expanded {
  max-height: 300px;
}

.service-header {
  background: #1890ff;
  color: white;
  text-align: center;
  padding: 10px 0;
  font-weight: bold;
}

.service-body {
  padding: 15px;
}

.qrcode-container {
  text-align: center;
  margin-bottom: 15px;
}

.qrcode-img {
  width: 120px;
  height: 120px;
  object-fit: contain;
}

.service-info {
  text-align: center;
}

.service-info p {
  margin: 5px 0;
  font-size: 14px;
  color: #333;
}

.service-link {
  display: inline-block;
  background: #1890ff;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 12px;
  margin-top: 5px;
  transition: background 0.3s;
}

.service-link:hover {
  background: #40a9ff;
}

/* Responsive */
@media (max-width: 768px) {
  .customer-service {
    display: none;
  }
  
  .header-content {
    height: 70px;
    padding: 0 20px;
  }
  
  .logo {
    font-size: 20px;
  }
  
  .hero-content {
    flex-direction: column;
    text-align: center;
    gap: 40px;
  }
  
  .hero-title {
    font-size: 48px;
    flex-direction: column;
    gap: 20px;
  }
  
  .vmware-logo {
    width: 60px;
    height: 60px;
  }
  
  .hero-subtitle {
    font-size: 24px;
    margin-bottom: 20px;
  }
  
  .hero-description {
    font-size: 18px;
    margin-bottom: 40px;
  }
  
  .hero-features ul {
    grid-template-columns: 1fr;
  }
  
  .features-showcase-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .showcase-item {
    padding: 30px 15px;
  }
  
  .showcase-icon {
    font-size: 36px;
  }
  
  .showcase-title {
    font-size: 20px;
  }
  
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 0 15px;
  }
  
  .advantages-grid {
    grid-template-columns: 1fr;
    padding: 0 15px;
  }
  
  .testimonials-grid {
    grid-template-columns: 1fr;
    padding: 0 15px;
  }
  
  .download-btn, .cta-button {
    padding: 25px 60px;
    font-size: 22px;
    min-width: 300px;
  }
  
  .footer-links {
    flex-direction: column;
    gap: 10px;
  }
  
  .logo-title-container {
    flex-direction: column;
    gap: 15px;
  }
  
  .hero-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .features-showcase-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 32px;
  }
  
  .hero-subtitle {
    font-size: 20px;
  }
  
  .hero-description {
    font-size: 16px;
  }
  
  .download-btn, .cta-button {
    padding: 20px 40px;
    font-size: 18px;
    min-width: 250px;
  }
  
  .hero-features li {
    padding: 15px;
    font-size: 14px;
  }
  
  .section-title {
    font-size: 32px;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 0 10px;
  }
}

/* 768px以下的样式 */
@media (max-width: 768px) {
  .hero-title {
    font-size: 48px;
  }
  
  .hero-subtitle {
    font-size: 24px;
  }
  
  .hero-description {
    font-size: 18px;
  }
  
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

/* 480px以下的样式 */
@media (max-width: 480px) {
  .hero-title {
    font-size: 36px;
  }
  
  .hero-subtitle {
    font-size: 20px;
  }
  
  .hero-description {
    font-size: 16px;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 0 10px;
  }
}
</style>